import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Award, Plus, Gift, Check, X, Sparkles, Lock, Upload, ChevronUp, ChevronDown, Pencil, Trash2, FolderCog, Archive, ArchiveRestore } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function toPascal(k) { return k ? k.split("-").map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join("") : "Award"; }
function CharmIcon({ name, className, style }) { const C = Icons[toPascal(name)] || Award; return <C className={className} style={style} />; }
function imgSrc(x) { return x?.image_url ? `${BACKEND}${x.image_url}` : null; }

function CharmArt({ charm, color = "#2FB7B5", size = 72, locked, pop }) {
  const src = imgSrc(charm);
  return (
    <div className={`relative rounded-full flex items-center justify-center shrink-0 ${pop ? "charm-pop" : ""} ${locked ? "charm-locked" : ""}`}
      style={{ width: size, height: size, background: `${color}1F`, boxShadow: locked ? "none" : `0 0 0 3px ${color}33` }}>
      {src ? <img src={src} alt={charm.name} className="w-full h-full object-cover rounded-full" />
        : <CharmIcon name={charm?.icon} className="text-teal-dark" style={{ width: size * 0.42, height: size * 0.42 }} />}
      {locked && <span className="absolute inset-0 flex items-center justify-center"><Lock className="w-5 h-5 text-gray-500" /></span>}
    </div>
  );
}

export default function Charms() {
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const isStaff = isDirector || user?.role === "teacher";
  const [charms, setCharms] = useState([]);
  const [cats, setCats] = useState([]);
  const [awarded, setAwarded] = useState([]);
  const [requests, setRequests] = useState([]);
  const [dancers, setDancers] = useState([]);
  const [charmFilter, setCharmFilter] = useState("active"); // active | archived | all (director)

  const loadCats = () => api.get("/charms/categories").then((r) => setCats(r.data));
  const load = () => {
    api.get("/charms").then((r) => setCharms(r.data));
    loadCats();
    api.get("/charms/awarded").then((r) => setAwarded(r.data)).catch(() => {});
    if (isStaff) {
      api.get("/charms/requests").then((r) => setRequests(r.data)).catch(() => {});
      api.get("/people/dancers").then((r) => setDancers(r.data)).catch(() => {});
    }
  };
  useEffect(load, []);

  const catMap = Object.fromEntries(cats.map((c) => [c.id, c]));
  const charmMap = Object.fromEntries(charms.map((c) => [c.id, c]));
  const dancerMap = Object.fromEntries(dancers.map((d) => [d.id, d]));
  const catColor = (id) => catMap[id]?.color || "#2FB7B5";

  // charm editor
  const [charmDlg, setCharmDlg] = useState(false);
  const emptyCharm = { name: "", description: "", icon: "award", category_id: "", dance_style: "", earning_requirements: "", repeatable: false, teacher_awardable: true, requires_approval: false, active: true };
  const [charmForm, setCharmForm] = useState(emptyCharm);
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const closeCharmDlg = () => { setCharmDlg(false); setCharmForm(emptyCharm); setEditId(null); setFile(null); load(); };
  const saveCharm = async () => {
    try {
      let id = editId;
      if (editId) await api.put(`/charms/${editId}`, charmForm);
      else { const r = await api.post("/charms", charmForm); id = r.data.id; setEditId(id); }
      if (file && id) {
        try { const fd = new FormData(); fd.append("file", file); await api.post(`/charms/${id}/image`, fd); }
        catch { toast.error("Charm saved, but the image could not be processed"); closeCharmDlg(); return; }
      }
      toast.success("Charm saved"); closeCharmDlg();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const openEdit = (c) => { setCharmForm({ ...emptyCharm, ...c }); setEditId(c.id); setFile(null); setCharmDlg(true); };

  // category editor
  const [catDlg, setCatDlg] = useState(false);
  const emptyCat = { name: "", description: "", color: "#2FB7B5", icon: "award", active: true };
  const [catForm, setCatForm] = useState(emptyCat);
  const [catEditId, setCatEditId] = useState(null);
  const openCatNew = () => { setCatForm(emptyCat); setCatEditId(null); setCatDlg(true); };
  const saveCat = async () => {
    try {
      let id = catEditId;
      if (catEditId) await api.put(`/charms/categories/${catEditId}`, catForm);
      else { const r = await api.post("/charms/categories", catForm); id = r.data.id; }
      toast.success("Category saved"); setCatDlg(false);
      await loadCats();
      if (!catEditId && id) setCharmForm((p) => ({ ...p, category_id: id }));
      setCatForm(emptyCat); setCatEditId(null);
    } catch (e) { toast.error("Error"); }
  };
  const toggleCatActive = async (c) => { await api.put(`/charms/categories/${c.id}`, { ...c, active: !c.active }); loadCats(); };
  const moveCat = async (idx, dir) => {
    const arr = [...cats]; const j = idx + dir; if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setCats(arr);
    await api.post("/charms/categories/reorder", { ordered_ids: arr.map((c) => c.id) });
  };
  const [reassignDlg, setReassignDlg] = useState(null); // category pending delete
  const [reassignTo, setReassignTo] = useState("");
  const [confirmDelCat, setConfirmDelCat] = useState(null);
  const doDeleteCat = async (c) => {
    try { await api.delete(`/charms/categories/${c.id}`); toast.success("Category deleted"); setConfirmDelCat(null); load(); }
    catch (e) { setConfirmDelCat(null); if (e.response?.status === 409) { setReassignDlg(c); setReassignTo(""); } else toast.error("Error"); }
  };
  const activeCats = cats.filter((c) => c.active !== false);
  const confirmReassign = async () => {
    if (!reassignTo) { toast.error("Choose a category to move charms into"); return; }
    await api.delete(`/charms/categories/${reassignDlg.id}?reassign_to=${reassignTo}`);
    toast.success("Charms reassigned & category deleted"); setReassignDlg(null); load();
  };

  // award
  const [awardDlg, setAwardDlg] = useState(false);
  const [awardForm, setAwardForm] = useState({ charm: null, dancer_id: "", reason: "", skill: "", note: "" });
  const openAward = (charm) => { setAwardForm({ charm, dancer_id: "", reason: "", skill: "", note: "" }); setAwardDlg(true); };
  const doAward = async () => {
    if (!awardForm.dancer_id) { toast.error("Choose a dancer"); return; }
    try {
      const res = await api.post("/charms/award", { charm_id: awardForm.charm.id, dancer_id: awardForm.dancer_id, reason: awardForm.reason, skill: awardForm.skill, note: awardForm.note });
      if (res.data.already_earned) toast.info("Dancer already earned this charm");
      else if (res.data.status === "Pending") toast.success("Sent to Director for approval");
      else toast.success("Charm awarded! 🎉");
      setAwardDlg(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const review = async (rid, action) => {
    try { await api.post(`/charms/requests/${rid}/review`, { action }); toast.success(action === "approve" ? "Charm approved 🎉" : "Request denied"); load(); }
    catch (e) { toast.error("Error"); }
  };

  // archive / delete charm
  const [charmDel, setCharmDel] = useState(null); // { item, usage }
  const archiveCharm = async (c, archived) => {
    try { await api.post(`/charms/${c.id}/${archived ? "archive" : "unarchive"}`); toast.success(archived ? "Charm archived" : "Charm restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const askDeleteCharm = async (c) => {
    try { const r = await api.get(`/charms/${c.id}/usage`); setCharmDel({ item: c, usage: r.data }); }
    catch { toast.error("Could not load usage"); }
  };
  const doDeleteCharm = async () => {
    try { const r = await api.delete(`/charms/${charmDel.item.id}`); toast.success(`Deleted "${r.data.deleted}"`); setCharmDel(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  // ---- Dancer / Parent library ----
  if (!isStaff) {
    const earnedBy = {}; awarded.forEach((a) => { earnedBy[a.charm_id] = a; });
    const earnedCount = charms.filter((c) => earnedBy[c.id]).length;
    return (
      <div className="stagger-in">
        <PageHeader title="My Charms" subtitle={`You've collected ${earnedCount} of ${charms.length} charms — keep dancing!`} testid="charms-page" />
        {charms.length === 0 ? <EmptyState icon={Award} title="No charms yet" testid="charms-empty" /> :
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {charms.map((c) => {
              const earned = earnedBy[c.id]; const col = catColor(c.category_id);
              return (
                <div key={c.id} data-testid={`charm-tile-${c.id}`} className={`relative bg-white rounded-2xl border p-5 text-center transition-all ${earned ? "border-gray-100 hover:-translate-y-1 hover:shadow-md" : "border-dashed border-gray-200"}`}>
                  {catMap[c.category_id] && <span className="absolute top-2.5 right-2.5 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: `${col}1F`, color: col }}>{catMap[c.category_id].name}</span>}
                  <div className="flex justify-center mb-3"><CharmArt charm={c} color={col} size={76} locked={!earned} pop={!!earned} /></div>
                  <p className={`font-head font-bold ${earned ? "" : "text-gray-400"}`}>{c.name}</p>
                  {earned ? (
                    <>
                      <p className="text-xs text-muted-foreground mt-1">{earned.note || earned.reason || c.description}</p>
                      <p className="text-[11px] text-teal-dark font-semibold mt-2">{earned.date ? new Date(earned.date).toLocaleDateString() : ""}{earned.awarded_by_name ? ` · ${earned.awarded_by_name}` : ""}</p>
                    </>
                  ) : <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1"><Lock className="w-3 h-3" />{c.earning_requirements || "Keep going to unlock!"}</p>}
                </div>
              );
            })}
          </div>}
      </div>
    );
  }

  // ---- Director / Teacher ----
  const pendingCount = requests.filter((r) => r.status === "Pending").length;
  return (
    <div className="stagger-in">
      <PageHeader title="Charm Library" subtitle="Design, organize, award and celebrate achievements." testid="charms-page"
        action={isDirector && <Button data-testid="add-charm-button" onClick={() => { setCharmForm(emptyCharm); setEditId(null); setFile(null); setCharmDlg(true); }} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />Add Charm</Button>} />

      <Tabs defaultValue="charms">
        <TabsList className="bg-white rounded-full p-1 mb-6 flex-wrap h-auto">
          <TabsTrigger value="charms" data-testid="charms-tab-all" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Library</TabsTrigger>
          <TabsTrigger value="requests" data-testid="charms-tab-requests" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Requests{pendingCount > 0 && ` (${pendingCount})`}</TabsTrigger>
          {isDirector && <TabsTrigger value="categories" data-testid="charms-tab-categories" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Categories</TabsTrigger>}
        </TabsList>

        <TabsContent value="charms">
          {isDirector && (
            <div className="flex gap-2 mb-4" data-testid="charm-filter">
              {[["active", "Active"], ["archived", `Archived${charms.filter((c) => c.archived).length ? ` (${charms.filter((c) => c.archived).length})` : ""}`], ["all", "All"]].map(([k, l]) => (
                <button key={k} data-testid={`charm-filter-${k}`} onClick={() => setCharmFilter(k)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${charmFilter === k ? "bg-black text-white border-black" : "bg-white"}`}>{l}</button>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(isDirector ? charms.filter((c) => charmFilter === "all" ? true : charmFilter === "archived" ? c.archived : !c.archived) : charms).map((c) => {
              const col = catColor(c.category_id);
              return (
                <div key={c.id} data-testid={`charm-card-${c.id}`} className={`bg-white rounded-2xl border border-gray-100 p-5 ${c.archived || c.active === false ? "opacity-50" : ""}`}>
                  <div className="flex items-start gap-3">
                    <CharmArt charm={c} color={col} size={56} />
                    <div className="min-w-0 flex-1">
                      <p className="font-head font-bold">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{catMap[c.category_id]?.name || "Uncategorized"}{c.dance_style ? ` · ${c.dance_style}` : ""}</p>
                    </div>
                  </div>
                  <p className="text-sm mt-3 text-muted-foreground line-clamp-2">{c.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {c.repeatable && <Badge variant="outline" className="rounded-full text-[10px]">Repeatable</Badge>}
                    {c.requires_approval && <Badge className="bg-yellow-50 text-yellow-700 rounded-full text-[10px]">Needs approval</Badge>}
                    {!c.teacher_awardable && <Badge className="bg-black text-white rounded-full text-[10px]">Director only</Badge>}
                    {c.active === false && <Badge className="bg-gray-100 text-gray-600 rounded-full text-[10px]">Inactive</Badge>}
                    {c.archived && <Badge className="bg-gray-200 text-gray-600 rounded-full text-[10px]">Archived</Badge>}
                  </div>
                  <div className="flex gap-2 mt-4">
                    {(isDirector || c.teacher_awardable) && !c.archived && <Button size="sm" onClick={() => openAward(c)} data-testid={`award-charm-${c.id}`} className="rounded-full flex-1 bg-teal hover:bg-teal-dark"><Gift className="w-3.5 h-3.5 mr-1" />Award</Button>}
                    {isDirector && <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0" title="Edit" data-testid={`edit-charm-${c.id}`} onClick={() => openEdit(c)}><Pencil className="w-3.5 h-3.5" /></Button>}
                    {isDirector && (c.archived
                      ? <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0" title="Restore" data-testid={`unarchive-charm-${c.id}`} onClick={() => archiveCharm(c, false)}><ArchiveRestore className="w-3.5 h-3.5" /></Button>
                      : <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0" title="Archive" data-testid={`archive-charm-${c.id}`} onClick={() => archiveCharm(c, true)}><Archive className="w-3.5 h-3.5" /></Button>)}
                    {isDirector && <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50" title="Delete" data-testid={`delete-charm-${c.id}`} onClick={() => askDeleteCharm(c)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="requests">
          {pendingCount === 0 ? <EmptyState icon={Sparkles} title="No pending requests" hint="Teacher charm requests appear here." /> :
            <div className="space-y-3">
              {requests.filter((r) => r.status === "Pending").map((r) => {
                const c = charmMap[r.charm_id] || {}; const d = dancerMap[r.dancer_id];
                return (
                  <div key={r.id} data-testid={`charm-request-${r.id}`} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <CharmArt charm={c} color={catColor(c.category_id)} size={44} />
                      <div><p className="font-head font-bold">{c.name} → {d ? `${d.first_name} ${d.last_name}` : "Dancer"}</p><p className="text-sm text-muted-foreground">{r.reason || r.note}</p></div>
                    </div>
                    {isDirector && <div className="flex gap-2">
                      <Button size="sm" data-testid={`approve-charm-${r.id}`} onClick={() => review(r.id, "approve")} className="rounded-full bg-teal hover:bg-teal-dark"><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" data-testid={`deny-charm-${r.id}`} onClick={() => review(r.id, "deny")} className="rounded-full"><X className="w-4 h-4" /></Button>
                    </div>}
                  </div>
                );
              })}
            </div>}
        </TabsContent>

        {isDirector && (
          <TabsContent value="categories">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground">Categories only organize charms — they don't set rank or value. Reorder, rename, recolor or retire them anytime.</p>
              <Button data-testid="add-category-button" onClick={openCatNew} className="rounded-full bg-teal hover:bg-teal-dark shrink-0"><Plus className="w-4 h-4 mr-2" />New Category</Button>
            </div>
            <div className="space-y-2">
              {cats.map((c, idx) => (
                <div key={c.id} data-testid={`category-row-${c.id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
                  <div className="flex flex-col">
                    <button data-testid={`cat-up-${c.id}`} onClick={() => moveCat(idx, -1)} disabled={idx === 0} className="disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                    <button data-testid={`cat-down-${c.id}`} onClick={() => moveCat(idx, 1)} disabled={idx === cats.length - 1} className="disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                  </div>
                  <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${c.color}1F` }}><CharmIcon name={c.icon} className="w-4 h-4" style={{ color: c.color }} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{c.name}{c.active === false && <span className="text-xs text-muted-foreground ml-2">(inactive)</span>}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.description}</p>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs"><Switch data-testid={`cat-active-${c.id}`} checked={c.active !== false} onCheckedChange={() => toggleCatActive(c)} /></label>
                  <Button size="sm" variant="outline" className="rounded-full" data-testid={`edit-category-${c.id}`} onClick={() => { setCatForm({ ...emptyCat, ...c }); setCatEditId(c.id); setCatDlg(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-category-${c.id}`} onClick={() => setConfirmDelCat(c)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Award dialog */}
      <Dialog open={awardDlg} onOpenChange={setAwardDlg}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><CharmArt charm={awardForm.charm} color={catColor(awardForm.charm?.category_id)} size={32} />Award: {awardForm.charm?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>Dancer</Label>
            <select data-testid="award-dancer-select" className="w-full border rounded-md h-10 px-3" value={awardForm.dancer_id} onChange={(e) => setAwardForm({ ...awardForm, dancer_id: e.target.value })}>
              <option value="">Select dancer…</option>{dancers.map((d) => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label>Related skill / achievement (optional)</Label><Input data-testid="award-skill" value={awardForm.skill} onChange={(e) => setAwardForm({ ...awardForm, skill: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Personalized note (optional)</Label><Textarea data-testid="award-note" value={awardForm.note} onChange={(e) => setAwardForm({ ...awardForm, note: e.target.value })} placeholder="Way to go!" /></div>
          <DialogFooter><Button data-testid="confirm-award-button" onClick={doAward} className="rounded-full bg-teal hover:bg-teal-dark">Award Charm</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Charm editor */}
      <Dialog open={charmDlg} onOpenChange={setCharmDlg}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editId ? "Edit" : "Add"} Charm</DialogTitle></DialogHeader>
          <div className="flex items-center gap-4">
            <CharmArt charm={{ ...charmForm, image_url: editId ? charmMap[editId]?.image_url : "" }} color={catColor(charmForm.category_id)} size={64} />
            <div>
              <input ref={fileRef} data-testid="charm-image-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()}><Upload className="w-3.5 h-3.5 mr-1" />{file ? "Change image" : "Upload artwork"}</Button>
              <p className="text-xs text-muted-foreground mt-1">{file ? file.name : "PNG, JPG or WebP"}</p>
            </div>
          </div>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="charm-name" value={charmForm.name} onChange={(e) => setCharmForm({ ...charmForm, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between"><Label>Category</Label><button type="button" data-testid="inline-add-category" onClick={openCatNew} className="text-xs font-semibold text-teal-dark hover:underline flex items-center gap-0.5"><Plus className="w-3 h-3" />New</button></div>
                <select data-testid="charm-category" className="w-full border rounded-md h-10 px-3" value={charmForm.category_id} onChange={(e) => setCharmForm({ ...charmForm, category_id: e.target.value })}><option value="">—</option>{activeCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
              </div>
              <div className="space-y-1.5"><Label>Dance style</Label><Input data-testid="charm-style" value={charmForm.dance_style} onChange={(e) => setCharmForm({ ...charmForm, dance_style: e.target.value })} placeholder="Ballet, Tap…" /></div>
            </div>
            <div className="space-y-1.5"><Label>Icon fallback (if no image)</Label><Input value={charmForm.icon} onChange={(e) => setCharmForm({ ...charmForm, icon: e.target.value })} placeholder="star, heart" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={charmForm.description} onChange={(e) => setCharmForm({ ...charmForm, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Earning requirements</Label><Textarea data-testid="charm-requirements" value={charmForm.earning_requirements} onChange={(e) => setCharmForm({ ...charmForm, earning_requirements: e.target.value })} placeholder="e.g. Master all Green level skills" /></div>
            <div className="flex flex-wrap gap-5">
              <label className="flex items-center gap-2 text-sm"><Switch checked={charmForm.active} onCheckedChange={(v) => setCharmForm({ ...charmForm, active: v })} />Active</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={charmForm.repeatable} onCheckedChange={(v) => setCharmForm({ ...charmForm, repeatable: v })} />Repeatable</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={charmForm.teacher_awardable} onCheckedChange={(v) => setCharmForm({ ...charmForm, teacher_awardable: v })} />Teacher awardable</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={charmForm.requires_approval} onCheckedChange={(v) => setCharmForm({ ...charmForm, requires_approval: v })} />Requires approval</label>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-charm-button" onClick={saveCharm} className="rounded-full bg-teal hover:bg-teal-dark">Save Charm</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category editor */}
      <Dialog open={catDlg} onOpenChange={setCatDlg}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><FolderCog className="w-5 h-5 text-teal" />{catEditId ? "Edit" : "New"} Category</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>Name</Label><Input data-testid="category-name" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Description (optional)</Label><Textarea data-testid="category-description" value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Color</Label><Input data-testid="category-color" type="color" value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} className="h-10 p-1" /></div>
            <div className="space-y-1.5"><Label>Icon (lucide name)</Label><Input data-testid="category-icon" value={catForm.icon} onChange={(e) => setCatForm({ ...catForm, icon: e.target.value })} placeholder="star, heart, trophy" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={catForm.active} onCheckedChange={(v) => setCatForm({ ...catForm, active: v })} />Active</label>
          <DialogFooter><Button data-testid="save-category-button" onClick={saveCat} className="rounded-full bg-teal hover:bg-teal-dark">Save Category</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign-before-delete dialog */}
      <Dialog open={!!reassignDlg} onOpenChange={(v) => !v && setReassignDlg(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Reassign charms</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">"{reassignDlg?.name}" has charms assigned. Move them to another category before deleting.</p>
          <select data-testid="reassign-select" className="w-full border rounded-md h-10 px-3" value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
            <option value="">Choose category…</option>{activeCats.filter((c) => c.id !== reassignDlg?.id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <DialogFooter><Button data-testid="confirm-reassign-button" onClick={confirmReassign} className="rounded-full bg-teal hover:bg-teal-dark">Reassign & Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Charm usage / delete dialog */}
      <Dialog open={!!charmDel} onOpenChange={(v) => !v && setCharmDel(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="charm-usage-dialog">
          <DialogHeader><DialogTitle className="font-head">{charmDel?.usage?.can_delete ? "Delete charm?" : "This charm has been awarded"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">"{charmDel?.usage?.name}"</p>
            <div className="rounded-xl border divide-y">
              {charmDel?.usage?.breakdown?.map((b, i) => (
                <div key={i} className="flex justify-between px-3 py-2"><span className="text-muted-foreground">{b.label}</span><span className="font-semibold" data-testid={`charm-usage-count-${i}`}>{b.count}</span></div>
              ))}
            </div>
            {charmDel?.usage?.can_delete
              ? <p>This charm has no awards or pending requests. Permanent deletion cannot be undone.</p>
              : <p className="text-amber-800 bg-amber-50 rounded-lg p-2.5" data-testid="charm-archive-note">Archive it instead so those awards stay visible in dancers' charm histories.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setCharmDel(null)}>Cancel</Button>
            {charmDel?.usage?.can_delete
              ? <Button data-testid="confirm-delete-charm" onClick={doDeleteCharm} className="rounded-full bg-red-600 hover:bg-red-700 text-white">Delete permanently</Button>
              : <Button data-testid="archive-instead-charm" onClick={() => { archiveCharm(charmDel.item, true); setCharmDel(null); }} className="rounded-full bg-teal hover:bg-teal-dark">Archive instead</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete (unused category) dialog */}
      <Dialog open={!!confirmDelCat} onOpenChange={(v) => !v && setConfirmDelCat(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Delete category?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Delete "{confirmDelCat?.name}"? This can't be undone.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" data-testid="cancel-delete-category" onClick={() => setConfirmDelCat(null)}>Cancel</Button>
            <Button data-testid="confirm-delete-category" onClick={() => doDeleteCat(confirmDelCat)} className="rounded-full bg-red-600 hover:bg-red-700 text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
